// src/components/RouterLayout.jsx
import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

// URL patterns that identify a DigiLocker deep-link callback.
// Matches both custom scheme (com.complymax.app://) and Android App Links (https://).
const DIGILOCKER_PATTERNS = [
  "digilocker-callback",
  "digilocker-return",
  "type=digilocker",
];

const isDigilockerUrl = (url) =>
  DIGILOCKER_PATTERNS.some((p) => url.includes(p));

const parseDeepLinkParams = (rawUrl) => {
  const empty = { client_id: "", status: "success" };
  if (!rawUrl) return empty;

  try {
    const parsed = new URL(rawUrl);

    // client_id may be in query string or in hash fragment
    const fromQuery =
      parsed.searchParams.get("client_id") ||
      parsed.searchParams.get("clientId") ||
      parsed.searchParams.get("reference_id") ||
      "";

    const fromHash = (() => {
      const hash = String(parsed.hash || "").replace(/^#/, "");
      if (!hash) return "";
      const hp = new URLSearchParams(hash);
      return (
        hp.get("client_id") ||
        hp.get("clientId") ||
        hp.get("reference_id") ||
        ""
      );
    })();

    return {
      client_id: String(fromQuery || fromHash).trim(),
      status: parsed.searchParams.get("status") || "success",
    };
  } catch {
    return empty;
  }
};

const RouterLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Android hardware back-button handler
  useEffect(() => {
    let listener;

    const setupListener = async () => {
      listener = await CapacitorApp.addListener("backButton", () => {
        const currentPath = location.pathname;

        if (
          currentPath === "/home" ||
          currentPath === "/user_dashboard" ||
          currentPath === "/app-login"
        ) {
          CapacitorApp.exitApp();
        } else {
          navigate(-1);
        }
      });
    };

    setupListener();

    return () => {
      if (listener && typeof listener.remove === "function") {
        listener.remove();
      }
    };
  }, [location.pathname, navigate]);

  // Deep-link / Android App Links handler
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let appUrlListener;

    const setupAppUrlListener = async () => {
      appUrlListener = await CapacitorApp.addListener(
        "appUrlOpen",
        ({ url }) => {
          if (!url) return;

          // Ignore unrelated deep links (future schemes, other features)
          if (!isDigilockerUrl(url)) return;

          const { client_id, status } = parseDeepLinkParams(url);

          // Always persist status so AadharSection can react correctly
          localStorage.setItem("digilocker_verification_status", status);

          if (client_id) {
            localStorage.setItem("digilocker_client_id", client_id);
          }

          // Navigate to profile — AadharSection will read localStorage on mount
          navigate("/user_dashboard/user_profile");
        }
      );
    };

    setupAppUrlListener();

    return () => {
      if (appUrlListener && typeof appUrlListener.remove === "function") {
        appUrlListener.remove();
      }
    };
  }, [navigate]);

  return <Outlet />;
};

export default RouterLayout;
